import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Flower2, Mail, Lock, Eye, EyeOff, Shield } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { toast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';

const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [needs2FA, setNeeds2FA] = useState(false);
  const [formData, setFormData] = useState({
    identifier: '',
    password: '',
    totp_code: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.identifier || !formData.password) {
      toast({
        title: 'Erreur',
        description: 'Veuillez remplir tous les champs',
        variant: 'destructive'
      });
      return;
    }

    if (needs2FA && !formData.totp_code) {
      toast({
        title: 'Erreur',
        description: 'Veuillez entrer le code 2FA',
        variant: 'destructive'
      });
      return;
    }

    setLoading(true);
    const result = await login(formData.identifier, formData.password, formData.totp_code || null);
    setLoading(false);

    if (result.requires_2fa) {
      setNeeds2FA(true);
      toast({
        title: 'Code 2FA requis',
        description: 'Entrez le code de votre application d\'authentification'
      });
      return;
    }

    if (result.success) {
      toast({
        title: 'Connexion réussie!',
        description: 'Bienvenue sur Fleur de Vie'
      });
      navigate('/dashboard');
    } else {
      toast({
        title: 'Erreur de connexion',
        description: result.error,
        variant: 'destructive'
      });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-pink-800 to-purple-900 flex items-center justify-center px-4">
      <Card className="w-full max-w-md p-8 bg-white/10 backdrop-blur-md border-white/20">
        <div className="text-center mb-8">
          <Flower2 className="w-16 h-16 text-pink-300 mx-auto mb-4" />
          <h1 className="text-3xl font-bold text-white mb-2">Fleur de Vie</h1>
          <h2 className="text-xl font-semibold text-white mb-2">Connexion</h2>
          <p className="text-pink-200 text-sm">Accédez à votre espace personnel</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="text-white text-sm mb-2 block">Email ou Téléphone</label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-pink-300" />
              <Input
                type="text"
                placeholder="votre.email@example.com ou +225..."
                value={formData.identifier}
                onChange={(e) => setFormData({...formData, identifier: e.target.value})}
                className="pl-12 bg-white/10 border-white/20 text-white placeholder:text-pink-300/50 h-12"
                disabled={needs2FA}
              />
            </div>
          </div>

          <div>
            <div className="flex justify-between items-center mb-2">
              <label className="text-white text-sm">Mot de passe</label>
              <Link to="/forgot-password" className="text-pink-300 text-sm hover:underline">
                Oublié ?
              </Link>
            </div>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-pink-300" />
              <Input
                type={showPassword ? 'text' : 'password'}
                placeholder="Votre mot de passe"
                value={formData.password}
                onChange={(e) => setFormData({...formData, password: e.target.value})}
                className="pl-12 pr-12 bg-white/10 border-white/20 text-white placeholder:text-pink-300/50 h-12"
                disabled={needs2FA}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-pink-300"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          {/* 2FA Code Field */}
          {needs2FA && (
            <div className="animate-in fade-in slide-in-from-top-2" data-testid="2fa-login-field">
              <label className="text-white text-sm mb-2 block flex items-center gap-2">
                <Shield className="w-4 h-4 text-green-400" />
                Code d'authentification (2FA)
              </label>
              <Input
                type="text"
                inputMode="numeric"
                placeholder="000000"
                maxLength={8}
                value={formData.totp_code}
                onChange={(e) => setFormData({...formData, totp_code: e.target.value.replace(/[^a-f0-9]/gi, '').slice(0, 8)})}
                className="bg-white/10 border-green-400/30 text-white text-center text-2xl tracking-[0.5em] font-mono h-14 placeholder:text-green-300/30"
                autoFocus
                data-testid="2fa-login-input"
              />
              <p className="text-green-300/70 text-xs mt-1.5">
                Entrez le code de Google Authenticator ou un code de secours
              </p>
            </div>
          )}

          {/* Auto-login info */}
          {!needs2FA && (
            <div className="flex items-center space-x-3">
              <div className="w-4 h-4 rounded-sm bg-pink-500 flex items-center justify-center">
                <svg className="w-3 h-3 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </div>
              <span className="text-white text-sm">
                Vous resterez connecté automatiquement
              </span>
            </div>
          )}

          <Button 
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-r from-pink-400 to-pink-600 hover:from-pink-500 hover:to-pink-700 text-white font-semibold py-6 rounded-xl disabled:opacity-50"
            data-testid="login-submit-btn"
          >
            {loading ? 'Connexion...' : needs2FA ? 'Vérifier le code' : 'Se connecter'}
          </Button>
        </form>

        <div className="mt-6 text-center">
          <p className="text-pink-200 text-sm">
            Pas encore de compte ?{' '}
            <Link
              to="/register"
              className="text-pink-300 font-semibold hover:underline"
            >
              Créer un compte
            </Link>
          </p>
        </div>
      </Card>
    </div>
  );
};

export default Login;
